import { ProductReferenceCalculationSource } from "@/features/diagnostics/resources/constants.ts";
import DiagnosticsStore from "@/features/diagnostics/store/diagnostics-store.ts";
import useCurrentProduct from "@/services/product/hooks/use-current-product.ts";
import usePowerSupplyDimmingTypeFilterOptions from "@/features/sub-families/hooks/use-power-supply-dimming-type-filter-options.ts";
import { useChangeFilterOption } from "@/features/sub-families/providers/change-filter-option-provider.tsx";
import {
    type ChangeValue,
    type Choice,
    ConfigurationTab,
    type FilterOption,
    FilterType,
    type ProductData,
    type PropertyConfigCode,
    type SmartNumberDescription,
    type SmartNumberMapping,
} from "@/features/sub-families/types";
import { createFilterOption } from "@/features/sub-families/utils/filters.ts";
import {
    getDimTypePowerSupplyResetMappingByProductScenario,
    getProductScenario,
    getProductSupply,
    type ProductScenario,
} from "@/features/sub-families/utils/power-supply-dimming-type.ts";
import { getEmptyConfigCode } from "@/features/sub-families/utils/smart-number.ts";
import type { TranslationKey } from "@/features/translations/types";
import useFirstRender from "@/hooks/use-first-render.ts";
import Logger from "@/lib/logger.ts";
import { getProductReferenceBySmartNumberMapping } from "@/lib/product-reference.ts";
import {
    smartNumberChangeMappingAction,
    smartNumberChangeMultipleMappingAction,
} from "@/redux/slices/smart-number-slice.ts";
import { useAppDispatch, useAppSelector } from "@/redux/store.ts";
import { type Nullable, type Nullish, SmartNumberDescriptor, type SmartNumberDescriptorKey } from "@/types";

type OnChangeFn = (configCode: PropertyConfigCode, isPossible: boolean) => void;

export const CONFIG_CODE_YES = "YES" as PropertyConfigCode;
export const CONFIG_CODE_NO = "NO" as PropertyConfigCode;

/**
 * Power Supply & Dimming Type Scenarios
 * @see (https://confluence.hosted-tools.com/display/DLT/Technical+Analysis%3A+Power+Supply+Scenarios - outdated tho below description is the correct one)
 *
 * @desc
 * Scenario 1: power_supply = included
 *       => NO PS, YES DIMTYPE
 *       => change: DIMTYPE in SN                               => S39_POWER = 00
 *       => e.g. 123039210500-9027S7-0404040404-0000
 *
 * Scenario 2: fixed DIMTYPE = EP
 *       1. HARDCODED YES
 *          => YES DIMTYPE
 *          => change: S39_POWER in SN
 *       2. HARDCODED NO
 *          => NO DIMTYPE
 *          => change: DO NOTHING                               => S39_POWER = 00
 *       => e.g. 12304721EP01-9027M4-0404040404-0000
 *
 * Scenario 3: converter = included + fixation ≠ system
 *       1. HARDCODED YES
 *          => YES DIMTYPE (options from dimming_types)
 *          => change: DIMTYPE in SN + DERIVED 'supply' in SN   => S39_POWER = derived 'supply' for the new product
 *       2. HARDCODED NO
 *          => YES DIMTYPE (options from dimming_types)
 *          => change: DIMTYPE in SN                            => S39_POWER = 00
 *       => e.g. 123036210500-9027S7-0404040404-0000
 *
 * Scenario 4: converter = included + fixation = system
 *       => NO PS, YES DIMTYPE
 *       => change: DIMTYPE in SN                               => S39_POWER = 00
 *       => e.g. 123053230500-90T1M4-04040404-0000
 */

export default function usePowerSupplyDimmingType() {
    const currentProduct = useCurrentProduct();
    const isFirstRender = useFirstRender();

    const { checkAndWarnAboutConfigurationChanges } = useChangeFilterOption();

    const getFilterOption = usePowerSupplyDimmingTypeFilterOptions();

    const dispatch = useAppDispatch();
    const smartNumberDescription = useAppSelector((state) => state.smartNumber.description);
    const smartNumberMapping = useAppSelector((state) => state.smartNumber.mapping);
    const products = useAppSelector((state) => state.products.items);

    if (!currentProduct || !smartNumberMapping) return null;

    const productScenario = getProductScenario(currentProduct);
    const configCodeYesNo = getPowerSupplyInclusionStatus(productScenario, smartNumberMapping, smartNumberDescription);

    let powerSupplyOption: Nullable<FilterOption> = null;
    let dimmingTypeOption: Nullable<FilterOption> = null;

    let onChangePowerSupply: Nullable<OnChangeFn> = null;
    let onChangeDimmingType: Nullable<OnChangeFn> = null;

    let selectedDimmingTypeLabel: Nullish<string> = null;

    let selectedPowerSupplyConfigCode: Nullish<PropertyConfigCode> = null;
    let selectedDimmingTypeConfigCode: Nullish<PropertyConfigCode> = null;

    const displayAlert = configCodeYesNo === CONFIG_CODE_YES;

    if (productScenario !== null) logScenario(productScenario, isFirstRender);

    switch (productScenario) {
        case 1:
        case 4: {
            powerSupplyOption = null;
            dimmingTypeOption = getFilterOption("dimming_type");

            selectedDimmingTypeLabel = getSelectedDimType(dimmingTypeOption, smartNumberMapping);

            onChangePowerSupply = null;
            onChangeDimmingType = (configCode: PropertyConfigCode, isPossible: boolean) => {
                const change: ChangeValue = { descriptor: SmartNumberDescriptor.DimType, configCode };

                if (!isPossible) {
                    checkAndWarnAboutConfigurationChanges(change);
                    return;
                }

                const emptyConfigCode = getEmptyConfigCode(SmartNumberDescriptor.PowerSupply, smartNumberDescription);

                if (!emptyConfigCode) {
                    Logger.warn("Could not determine empty config code!");
                    DiagnosticsStore.collectEventLog(
                        "warning",
                        `Could not create empty config code for ${SmartNumberDescriptor.PowerSupply}`,
                    );

                    return;
                }

                dispatch(
                    smartNumberChangeMultipleMappingAction([
                        change, // Change the value for dimming types
                        { descriptor: SmartNumberDescriptor.PowerSupply, configCode: emptyConfigCode }, // Reset power supply
                    ]),
                );
            };

            break;
        }

        case 2: {
            // * TL;DL;
            // * Dimming Type will be mapped to S39_POWER. Therefore, all dimming types will be using the descriptor for POWER SUPPLY!

            const dimmingTypeDescriptor = SmartNumberDescriptor.PowerSupply;

            const isConfigCodeYes = configCodeYesNo === CONFIG_CODE_YES;

            const dimmingTypes = getFilterOption("supply");

            powerSupplyOption = createYesNoChoices(SmartNumberDescriptor.PowerSupply);
            dimmingTypeOption = isConfigCodeYes ? dimmingTypes : null;

            selectedPowerSupplyConfigCode = configCodeYesNo;
            selectedDimmingTypeConfigCode = getSelectedConfigCode(smartNumberMapping, dimmingTypeDescriptor);

            selectedDimmingTypeLabel = getSelectedPowerSupply(dimmingTypes, smartNumberMapping);

            onChangePowerSupply = (configCode: PropertyConfigCode) => {
                let dimmingTypeConfigCode: Nullable<PropertyConfigCode> = null;

                if (configCode === CONFIG_CODE_YES) {
                    // Select the first available dimming type to add to the power supply definition
                    dimmingTypeConfigCode = dimmingTypes?.choices[0]?.configCode ?? null;
                } else if (configCode === CONFIG_CODE_NO) {
                    // Reset the dimming type back to the empty value
                    const resetMapping = getDimTypePowerSupplyResetMappingByProductScenario(
                        productScenario,
                        smartNumberDescription,
                    );

                    dimmingTypeConfigCode = resetMapping?.configCode ?? null;
                }

                if (!dimmingTypeConfigCode) return;

                dispatch(
                    smartNumberChangeMappingAction({
                        descriptor: dimmingTypeDescriptor,
                        configCode: dimmingTypeConfigCode,
                    }),
                );
            };

            onChangeDimmingType = (configCode: PropertyConfigCode) => {
                dispatch(smartNumberChangeMappingAction({ descriptor: dimmingTypeDescriptor, configCode }));
            };

            break;
        }

        case 3: {
            const isConfigCodeYes = configCodeYesNo === CONFIG_CODE_YES;

            const dimmingTypes = getFilterOption("dimming_type");

            powerSupplyOption = createYesNoChoices(SmartNumberDescriptor.PowerSupply);
            dimmingTypeOption = dimmingTypes;

            selectedPowerSupplyConfigCode = configCodeYesNo;
            selectedDimmingTypeConfigCode = getSelectedConfigCode(smartNumberMapping, SmartNumberDescriptor.DimType);

            selectedDimmingTypeLabel = getSelectedDimType(dimmingTypes, smartNumberMapping);

            onChangePowerSupply = (configCode: PropertyConfigCode) => {
                let mapping: Nullable<ChangeValue> = null;

                if (configCode === CONFIG_CODE_YES && selectedDimmingTypeConfigCode) {
                    const productByDimType = getCurrentProductByDimType(
                        selectedDimmingTypeConfigCode,
                        smartNumberMapping,
                        smartNumberDescription,
                        products,
                    );

                    const supply = getProductSupply(productByDimType);

                    if (supply) {
                        mapping = { descriptor: SmartNumberDescriptor.PowerSupply, configCode: supply.code };
                    }
                } else if (configCode === CONFIG_CODE_NO) {
                    // Reset the config code back to the empty value
                    const psConfigCode = getEmptyConfigCode(SmartNumberDescriptor.PowerSupply, smartNumberDescription);
                    if (psConfigCode) {
                        mapping = { descriptor: SmartNumberDescriptor.PowerSupply, configCode: psConfigCode };
                    }
                }

                if (mapping) {
                    dispatch(smartNumberChangeMappingAction(mapping));
                }
            };

            onChangeDimmingType = (configCode: PropertyConfigCode) => {
                const mappings: ChangeValue[] = [{ descriptor: SmartNumberDescriptor.DimType, configCode }];

                if (isConfigCodeYes) {
                    const productByDimType = getCurrentProductByDimType(
                        configCode,
                        smartNumberMapping,
                        smartNumberDescription,
                        products,
                    );

                    const supply = getProductSupply(productByDimType);

                    if (supply) {
                        mappings.push({ descriptor: SmartNumberDescriptor.PowerSupply, configCode: supply.code });
                    }
                }

                dispatch(smartNumberChangeMultipleMappingAction(mappings));
            };

            break;
        }

        default:
            debug("Unhandled scenario!");
            break;
    }

    return {
        configCodeYesNo,
        powerSupplyOption,
        dimmingTypeOption,
        selectedDimmingTypeLabel,
        onChangePowerSupply,
        onChangeDimmingType,
        displayAlert,
        selectedPowerSupplyConfigCode: selectedPowerSupplyConfigCode ?? undefined,
        selectedDimmingTypeConfigCode: selectedDimmingTypeConfigCode ?? undefined,
    };
}

// region Helpers
function getSelectedDimType(filterOption: Nullable<FilterOption>, smartNumberMapping: Nullable<SmartNumberMapping>) {
    return getSelectedLabelByDescriptor(filterOption, smartNumberMapping, SmartNumberDescriptor.DimType);
}

function getSelectedPowerSupply(
    filterOption: Nullable<FilterOption>,
    smartNumberMapping: Nullable<SmartNumberMapping>,
) {
    return getSelectedLabelByDescriptor(filterOption, smartNumberMapping, SmartNumberDescriptor.PowerSupply);
}

function getSelectedConfigCode(smartNumberMapping: Nullable<SmartNumberMapping>, descriptor: SmartNumberDescriptorKey) {
    return smartNumberMapping?.[descriptor];
}

function getSelectedLabelByDescriptor(
    filterOption: Nullable<FilterOption>,
    smartNumberMapping: Nullable<SmartNumberMapping>,
    descriptor: SmartNumberDescriptorKey,
) {
    return filterOption?.choices.find((choice) => choice.configCode === smartNumberMapping?.[descriptor])?.label;
}

function createYesNoChoices(descriptor: SmartNumberDescriptorKey): FilterOption {
    return createFilterOption({
        label: "power_supply_included",
        tab: ConfigurationTab.Light,
        filterType: FilterType.Radio,
        descriptor,
        choices: [createChoice("no", CONFIG_CODE_NO), createChoice("yes", CONFIG_CODE_YES)],
    });
}

function createChoice(label: TranslationKey, configCode: PropertyConfigCode): Choice {
    return {
        label,
        value: configCode,
        configCode,
        description: null,
        image: null,
        sortKey: null,
        tooltip: null,
    };
}

function getPowerSupplyInclusionStatus(
    productScenario: Nullable<ProductScenario>,
    smartNumberMapping: Nullable<SmartNumberMapping>,
    smartNumberDescription: Nullable<SmartNumberDescription>,
): Nullable<PropertyConfigCode> {
    const isScenario2 = productScenario === 2;
    const isScenario3 = productScenario === 3;

    if (!(isScenario2 || isScenario3)) return null;
    if (!smartNumberMapping || !smartNumberDescription) return null;

    const powerSupplyConfigCode = getSelectedConfigCode(smartNumberMapping, SmartNumberDescriptor.PowerSupply);
    const emptyPowerSupplyCode = getEmptyConfigCode(SmartNumberDescriptor.PowerSupply, smartNumberDescription);

    // When S39_POWER === 00, we preselect the NO option
    return powerSupplyConfigCode === emptyPowerSupplyCode ? CONFIG_CODE_NO : CONFIG_CODE_YES;
}

function getCurrentProductByDimType(
    dimTypeConfigCode: PropertyConfigCode,
    smartNumberMapping: Nullable<SmartNumberMapping>,
    smartNumberDescription: Nullable<SmartNumberDescription>,
    products: ProductData,
) {
    if (!smartNumberMapping || !smartNumberDescription) return null;

    const remappedSmartNumberMapping = structuredClone(smartNumberMapping);
    remappedSmartNumberMapping[SmartNumberDescriptor.DimType] = dimTypeConfigCode;

    const reference = getProductReferenceBySmartNumberMapping(
        remappedSmartNumberMapping,
        smartNumberDescription,
        { source: ProductReferenceCalculationSource.PowerSupplyDimmingType, products },
    );

    if (!reference) return null;

    return products[reference] ?? null;
}

function debug(...messages: unknown[]) {
    Logger.debug("[POWER SUPPLY / DIM_TYPE]", ...messages);
}

function logScenario(scenario: number, isFirstRender: boolean) {
    if (!isFirstRender) return;
    debug(`Scenario ${scenario}`);
    DiagnosticsStore.collectEventLog("info", `[Product Scenario] ${scenario}`);
}
// endregion
